import React, { useCallback, useRef, useState } from 'react';
import cn from '@/utils/cn';
import SPASelect from './SPASelect';
import type { FilterProperty, FilterRowData, OperatorKey, RansackSearch, SelectOption } from './filterTypes';
import { BOOLEAN_OPTIONS, OPERATORS } from './filterTypes';

export interface InitialFilter {
  property: string;
  operator: string;
  value?: string;
}

interface AdvancedFiltersProps {
  /** What Clear resets to (a table's built-in default filter). Falls back to one empty row. */
  clearFilters?: InitialFilter[];
  initialFilters?: InitialFilter[];
  onFilterChange: (search: RansackSearch) => void;
  onRowsChange?: (rows: FilterRowData[]) => void;
  properties: FilterProperty[];
}

const smallButtonClass = cn(
  'tw-px-2 tw-py-1 tw-text-xs tw-rounded tw-border tw-border-solid tw-transition-colors tw-cursor-pointer',
  'tw-bg-transparent tw-border-spa-border tw-text-spa-secondary',
  'hover:tw-bg-spa-bg-hover hover:tw-text-spa-on-bg',
);

const toSelectOptions = (properties: FilterProperty[]): SelectOption[] => (
  properties.map(p => ({ label: p.label, value: p.value }))
);

const toOperatorOptions = (operators: OperatorKey[]): SelectOption[] => (
  operators.map(op => ({ label: OPERATORS[op], value: op }))
);

const booleanSelectOptions: SelectOption[] = BOOLEAN_OPTIONS.map(o => ({ label: o.label, value: o.value }));

export const buildRansackSearch = (rows: FilterRowData[]): RansackSearch => {
  const search: RansackSearch = {};

  rows.forEach((row) => {
    if (!row.property || !row.operator || !row.value) return;

    let propertyValue = row.property;

    // For negative operators, replace _or_ with _and_ to negate OR chaining
    // (matches legacy FilterRow.jsx behavior)
    if (row.operator.startsWith('not')) {
      propertyValue = propertyValue.replace(/_or_/g, '_and_');
    }

    search[`${propertyValue}_${row.operator}`] = row.value;
  });

  return search;
};

interface FilterRowComponentProps {
  canRemove: boolean;
  onChange: (id: number, updates: Partial<FilterRowData>) => void;
  onRemove: (id: number) => void;
  properties: FilterProperty[];
  row: FilterRowData;
}

const toSelectedOption = (value: string, label: string): SelectOption | null => {
  if (!value) return null;

  return { label, value };
};

const toSelectedValueOption = (
  value: string,
  isBoolean: boolean,
  options: SelectOption[],
): SelectOption | null => {
  if (!value) return null;
  if (isBoolean) return booleanSelectOptions.find(o => o.value === value) ?? null;

  return options.find(o => o.value === value) ?? { label: value, value };
};

const FilterRowComponent = ({ canRemove, onChange, onRemove, properties, row }: FilterRowComponentProps) => {
  const selectedProperty = properties.find(p => p.value === row.property);
  const operators = selectedProperty?.operators ?? [];
  const valueOptions = selectedProperty?.options ?? [];
  const operatorIsFixed = operators.length <= 1;

  const propertyValue = toSelectedOption(row.property, selectedProperty?.label ?? '');
  const operatorValue = toSelectedOption(row.operator, OPERATORS[row.operator as OperatorKey] ?? '');
  const valueSelectValue = toSelectedValueOption(row.value, selectedProperty?.isBoolean ?? false, valueOptions);

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
      <div className="tw-w-44 tw-flex-shrink-0">
        <SPASelect<SelectOption>
          isClearable={false}
          options={toSelectOptions(properties)}
          placeholder="Property..."
          value={propertyValue}
          onChange={(opt) => {
            const prop = properties.find(p => p.value === opt?.value);
            const autoOperator = prop?.operators.length === 1 ? prop.operators[0] : '';
            onChange(row.id, { operator: autoOperator, property: opt?.value ?? '', value: '' });
          }}
        />
      </div>

      <div className="tw-w-40 tw-flex-shrink-0">
        <SPASelect<SelectOption>
          components={operatorIsFixed ? { DropdownIndicator: null, IndicatorSeparator: null } : undefined}
          isClearable={false}
          isDisabled={!row.property || operatorIsFixed}
          options={toOperatorOptions(operators)}
          placeholder="Operator..."
          value={operatorValue}
          onChange={opt => onChange(row.id, { operator: opt?.value ?? '', value: '' })}
        />
      </div>

      <div className="tw-w-80">
        <SPASelect<SelectOption>
          isClearable={!selectedProperty?.isBoolean}
          isDisabled={!row.operator}
          options={selectedProperty?.isBoolean ? booleanSelectOptions : valueOptions}
          placeholder="Value..."
          value={valueSelectValue}
          onChange={opt => onChange(row.id, { value: opt?.value ?? '' })}
        />
      </div>

      {canRemove && (
        <button
          className={cn(
            'tw-flex tw-items-center tw-justify-center tw-flex-shrink-0',
            'tw-h-9 tw-w-9 tw-rounded tw-border tw-border-solid tw-transition-colors tw-cursor-pointer',
            'tw-bg-transparent tw-border-spa-border tw-text-spa-secondary tw-text-base tw-leading-none',
            'hover:tw-bg-spa-bg-hover hover:tw-text-spa-on-bg',
          )}
          title="Remove filter"
          type="button"
          onClick={() => onRemove(row.id)}
        >
          &times;
        </button>
      )}
    </div>
  );
};

// AIDEV-NOTE: Reusable server-side advanced filters for SPA tables.
// Builds Ransack search params for PaginationController.
// Consumers provide a FilterProperty[] config with options pre-populated.
const AdvancedFilters = ({
  clearFilters, initialFilters, onFilterChange, onRowsChange, properties,
}: AdvancedFiltersProps) => {
  const nextId = useRef(1);

  const toRows = (filters?: InitialFilter[]): FilterRowData[] => {
    if (!filters?.length) return [{ id: nextId.current++, operator: '', property: '', value: '' }];

    return filters.map(f => ({
      id: nextId.current++,
      operator: f.operator,
      property: f.property,
      value: f.value ?? '',
    }));
  };

  const [rows, setRows] = useState<FilterRowData[]>(() => toRows(initialFilters));

  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const debouncedFilterChange = useCallback((search: RansackSearch) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onFilterChange(search), 400);
  }, [onFilterChange]);

  const fireCallbacks = (next: FilterRowData[]) => {
    debouncedFilterChange(buildRansackSearch(next));
    onRowsChange?.(next);
  };

  const handleRowChange = (id: number, updates: Partial<FilterRowData>) => {
    const next = rowsRef.current.map(r => (r.id === id ? { ...r, ...updates } : r));
    setRows(next);
    fireCallbacks(next);
  };

  const handleAddRow = () => {
    setRows(prev => [...prev, { id: nextId.current++, operator: '', property: '', value: '' }]);
  };

  const handleRemoveRow = (id: number) => {
    const next = rowsRef.current.filter(r => r.id !== id);
    setRows(next);
    fireCallbacks(next);
  };

  const handleClear = () => {
    const next = toRows(clearFilters);
    setRows(next);
    fireCallbacks(next);
  };

  const isCleared = clearFilters?.length
    ? rows.length === clearFilters.length && rows.every((row, i) => (
      row.property === clearFilters[i].property
        && row.operator === clearFilters[i].operator
        && row.value === (clearFilters[i].value ?? '')
    ))
    : !rows.some(r => r.property && r.operator && r.value);

  return (
    <div
      className={cn(
        'tw-mb-4 tw-rounded tw-border tw-border-solid tw-border-spa-border-secondary',
        'tw-bg-spa-bg-secondary tw-p-4',
      )}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <span className="tw-text-sm tw-font-semibold tw-text-spa-secondary tw-uppercase tw-tracking-wider">
          Filters
        </span>
        {!isCleared && (
          <button
            className={cn(smallButtonClass, 'tw-text-spa-accent-text')}
            type="button"
            onClick={handleClear}
          >
            Clear
          </button>
        )}
      </div>

      {rows.map(row => (
        <FilterRowComponent
          key={row.id}
          canRemove={rows.length > 1}
          properties={properties}
          row={row}
          onChange={handleRowChange}
          onRemove={handleRemoveRow}
        />
      ))}

      <button
        className={smallButtonClass}
        type="button"
        onClick={handleAddRow}
      >
        + Add Filter
      </button>
    </div>
  );
};

export default AdvancedFilters;
